import { useState, useCallback } from 'react';
import { validateFilesBatch, DEFAULT_VALIDATION_CONFIG } from '../../utils/fileValidation';

// Types for validation module
export interface UploadedFile {
  id: string;
  file: File;
  size: string;
  status?: 'QUEUING' | 'VALIDATING' | 'UPLOADING' | 'PROCESSING' | 'COMPLETE' | 'FAILED';
  validationResult?: {
    security?: {
      mimeTypeValidated: boolean;
      fileSizeChecked: boolean;
      signatureVerified: boolean;
      contentScanned: boolean;
      svgSanitized: boolean;
      sessionStorageVerified: boolean;
    };
    warnings?: string[];
    errors?: string[];
    processingTime?: string;
  };
}

export interface InvalidFile {
  file: File;
  errors: string[];
}

export interface FileWithWarnings {
  file: File;
  warnings: string[];
}

export interface ValidationState {
  files: UploadedFile[];
  invalidFiles: InvalidFile[];
  filesWithWarnings: FileWithWarnings[];
  duplicateFiles: File[];
  isValidating: boolean;
}

export interface ValidationActions {
  processFiles: (fileList: FileList | File[]) => Promise<void>;
  handleRemoveFile: (fileId: string) => void;
  handleRemoveError: (errorId: string) => void;
  handleClearAll: () => void;
  handleClearErrors: () => void;
  formatFileSize: (bytes: number) => string;
  updateFileStatus: (fileId: string, status: UploadedFile['status']) => void;
  updateFileValidationResult: (fileId: string, result: UploadedFile['validationResult']) => void;
}

export interface UseUploadValidationReturn extends ValidationState, ValidationActions {}

/**
 * Hook untuk menangani validasi file upload dengan isolasi penuh
 * Sesuai dengan todo: "Refactor validasi upload zone ke modul terpisah (useUploadValidation.ts) untuk isolasi fitur"
 * 
 * Tanggung jawab:
 * - File validation dan processing
 * - Error dan warning management
 * - File size formatting
 * - State management untuk validation results
 * 
 * TIDAK menangani:
 * - AI Settings (handled by useAISettings)
 * - Upload logic (handled by useFileProcessing)
 * - UI states seperti drag/drop (handled by useFileProcessing)
 */
export const useUploadValidation = (): UseUploadValidationReturn => {
  // Validation states - HANYA untuk validation logic
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [invalidFiles, setInvalidFiles] = useState<InvalidFile[]>([]);
  const [filesWithWarnings, setFilesWithWarnings] = useState<FileWithWarnings[]>([]);
  const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Utility function untuk format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Main validation logic - CORE RESPONSIBILITY
  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    setIsValidating(true);
    
    try {
      const filesToValidate = Array.from(fileList);
      const existingFiles = files.map(f => f.file);
      
      const validation = await validateFilesBatch(
        filesToValidate,
        existingFiles,
        DEFAULT_VALIDATION_CONFIG
      );
      
      // Process validation results
      const newFiles: UploadedFile[] = validation.validFiles.map(file => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        size: formatFileSize(file.size),
        status: 'QUEUING' // Initial status
      }));
      
      const newInvalidFiles: InvalidFile[] = validation.invalidFiles;
      const newFilesWithWarnings: FileWithWarnings[] = validation.filesWithWarnings;
      const newDuplicateFiles: File[] = validation.duplicateFiles;
      
      // Handle batch errors
      if (validation.batchErrors.length > 0) {
        if (filesToValidate.length > 0) {
          newInvalidFiles.push({
            file: filesToValidate[0],
            errors: validation.batchErrors
          });
        }
      }
      
      // Update states
      setFiles(prev => [...prev, ...newFiles]);
      setInvalidFiles(prev => [...prev, ...newInvalidFiles]);
      setFilesWithWarnings(prev => [...prev, ...newFilesWithWarnings]);
      setDuplicateFiles(prev => [...prev, ...newDuplicateFiles]);
      
    } catch (error) {
      console.error('File validation error:', error);
      // Fallback: treat all files as invalid
      const fallbackInvalidFiles: InvalidFile[] = Array.from(fileList).map(file => ({
        file,
        errors: ['Validation failed due to system error']
      }));
      setInvalidFiles(prev => [...prev, ...fallbackInvalidFiles]);
    }
    
    setIsValidating(false);
  }, [files, formatFileSize]);

  // File removal logic
  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  // Error removal logic - COMPLEX but isolated
  const handleRemoveError = useCallback((errorId: string) => {
    try {
      if (errorId.startsWith('file-')) {
        // Remove specific file error
        const [, , fileIndex, errorIndex] = errorId.split('-');
        setInvalidFiles(prev => {
          const newInvalidFiles = [...prev];
          const targetFileIndex = parseInt(fileIndex);
          const targetErrorIndex = parseInt(errorIndex);
          
          if (newInvalidFiles[targetFileIndex]) {
            newInvalidFiles[targetFileIndex] = {
              ...newInvalidFiles[targetFileIndex],
              errors: newInvalidFiles[targetFileIndex].errors.filter((_, idx) => idx !== targetErrorIndex)
            };
            
            // Remove file from invalidFiles if no errors left
            if (newInvalidFiles[targetFileIndex].errors.length === 0) {
              newInvalidFiles.splice(targetFileIndex, 1);
            }
          }
          
          return newInvalidFiles;
        });
      } else if (errorId.startsWith('warning-')) {
        // Remove specific warning
        const [, , fileIndex, warningIndex] = errorId.split('-');
        setFilesWithWarnings(prev => {
          const newFilesWithWarnings = [...prev];
          const targetFileIndex = parseInt(fileIndex);
          const targetWarningIndex = parseInt(warningIndex);
          
          if (newFilesWithWarnings[targetFileIndex]) {
            newFilesWithWarnings[targetFileIndex] = {
              ...newFilesWithWarnings[targetFileIndex],
              warnings: newFilesWithWarnings[targetFileIndex].warnings.filter((_, idx) => idx !== targetWarningIndex)
            };
            
            // Remove file from fileWarningsList if no warnings left
            if (newFilesWithWarnings[targetFileIndex].warnings.length === 0) {
              newFilesWithWarnings.splice(targetFileIndex, 1);
            }
          }
          
          return newFilesWithWarnings;
        });
      } else if (errorId.startsWith('dup-')) {
        // Remove specific duplicate warning
        const [, , dupIndex] = errorId.split('-');
        setDuplicateFiles(prev => {
          const newDuplicateFiles = [...prev];
          const targetIndex = parseInt(dupIndex);
          newDuplicateFiles.splice(targetIndex, 1);
          return newDuplicateFiles;
        });
      }
    } catch (error) {
      console.error('Error removing validation error:', error);
      // Fallback: do nothing to prevent crashes
    }
  }, []);

  // Clear all validation results
  const handleClearAll = useCallback(() => {
    setFiles([]);
    setInvalidFiles([]);
    setFilesWithWarnings([]);
    setDuplicateFiles([]);
  }, []);

  // Clear only errors and warnings, keep valid files
  const handleClearErrors = useCallback(() => {
    setInvalidFiles([]);
    setFilesWithWarnings([]);
    setDuplicateFiles([]);
  }, []);

  // Update file status
  const updateFileStatus = useCallback((fileId: string, status: UploadedFile['status']) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId 
        ? { ...file, status }
        : file
    ));
  }, []);

  // Update file validation result
  const updateFileValidationResult = useCallback((fileId: string, result: UploadedFile['validationResult']) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId 
        ? { ...file, validationResult: result }
        : file
    ));
  }, []);

  return {
    // State
    files,
    invalidFiles,
    filesWithWarnings,
    duplicateFiles,
    isValidating,
    
    // Actions
    processFiles,
    handleRemoveFile,
    handleRemoveError,
    handleClearAll,
    handleClearErrors,
    formatFileSize,
    updateFileStatus,
    updateFileValidationResult
  };
};